import React from 'react';
import { Col, Descriptions, Image, Row } from 'antd';
import Contract from '../classes/contract';
import logo from '../../assets/img/logo-viasat-group.png';
import { StringFormats } from '../config/config';

const { shortDateFormat } = StringFormats;

interface IProps {
    data: Contract;
    translator: any;
}

class ContractSummary extends React.Component<IProps> {
    render() {
        const t = this.props.translator;
        const formatter = new Intl.NumberFormat('it-IT', {
            style: 'currency',
            currency: 'EUR',

            // These options are needed to round to whole numbers if that's what you want.
            //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
            //maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
        });
        return (
            <div className="center">
                <Image className="printable-header" src={logo} width={200} />
                <h2 className="font-weight-600 title-content">{t('commonClass.summary')}</h2>
                <Row>
                    <Col xs={24} sm={24} md={12} lg={8} xl={8}>
                        <Descriptions
                            bordered
                            column={{ xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}
                            style={{ padding: 5 }}
                            title={t('commonClass.package')}
                        >
                            <Descriptions.Item label={t('commonClass.product')}>
                                {t('sicuri&protetti.packages.' + this.props.data.package.productId + '.name')}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('commonClass.period')}>{t(this.props.data.package.period)}</Descriptions.Item>
                            <Descriptions.Item label={t('commonClass.options')}>
                                {this.props.data.package.options.length === 0 ? (
                                    <i>{t('errorMessages.noOptionSel')}</i>
                                ) : (
                                    this.props.data.package.options.map((option) => {
                                        return t('sicuri&protetti.options.' + option + '.name') + '\n';
                                    })
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label={<strong className="uppercase italic">{t('commonClass.finalPrice')}</strong>}>
                                {formatter.format(this.props.data.package.totalPrice)}
                            </Descriptions.Item>
                        </Descriptions>
                    </Col>

                    <Col xs={24} sm={24} md={12} lg={8} xl={8}>
                        <Descriptions
                            bordered
                            column={{ xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}
                            style={{ padding: 5 }}
                            title={t('vehicleClass.vehicle')}
                        >
                            <Descriptions.Item label={t('vehicleClass.licensePlate')}>
                                {this.props.data.vehicle.licensePlate}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('vehicleClass.brand')}>{this.props.data.vehicle.brand}</Descriptions.Item>
                            <Descriptions.Item label={t('vehicleClass.model')}>{this.props.data.vehicle.model}</Descriptions.Item>
                            <Descriptions.Item label={t('vehicleClass.color')}>{this.props.data.vehicle.color}</Descriptions.Item>
                            <Descriptions.Item label={t('vehicleClass.regDate')}>
                                {this.props.data.vehicle.registrationDate!.format(shortDateFormat)}
                            </Descriptions.Item>
                        </Descriptions>
                    </Col>

                    <Col xs={24} sm={24} md={12} lg={8} xl={8}>
                        <Descriptions
                            bordered
                            column={{ xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}
                            style={{ padding: 5 }}
                            title={t('commonClass.contractor')}
                        >
                            {this.props.data.customer.isBusiness === 0 ? (
                                <>
                                    <Descriptions.Item label={t('commonClass.contractor')}>
                                        <strong>{t('customerClass.firstname')} :</strong> {this.props.data.customer.firstname}{' '}
                                        {this.props.data.customer.surname}
                                        <br />
                                        <strong>{t('customerClass.birthPlace')} :</strong> {this.props.data.customer.cityOfBirth} (
                                        {this.props.data.customer.stateOfBirth})
                                        <br />
                                        <strong>{t('customerClass.dateOfBirth')} :</strong>{' '}
                                        {this.props.data.customer.dateOfBirth!.format(shortDateFormat)}
                                        <br />
                                        <strong>{t('customerClass.fiscalIdSN')} :</strong> {this.props.data.customer.fiscalId}
                                    </Descriptions.Item>
                                    <Descriptions.Item label={t('customerClass.residencePlace')}>
                                        {this.props.data.customer.addressResidence} - {this.props.data.customer.cityResidence} (
                                        {this.props.data.customer.provinceResidenceId}) - {this.props.data.customer.postalCodeResidence}
                                    </Descriptions.Item>
                                    <Descriptions.Item label={t('customerClass.identityDoc')}>
                                        <strong>{t('customerClass.docType')} :</strong> {this.props.data.customer.docType}
                                        <br />
                                        <strong>{t('customerClass.docNum')} :</strong> {this.props.data.customer.docNumber}
                                        <br />
                                        <strong>{t('customerClass.releasedBy')} :</strong> {this.props.data.customer.releasedBy}
                                        <br />
                                        <strong>{t('customerClass.releasedOn')} :</strong>{' '}
                                        {this.props.data.customer.releasedOn!.format('DD/MM/yyyy')}
                                    </Descriptions.Item>
                                </>
                            ) : (
                                <>
                                    <Descriptions.Item label={t('customerClass.businessName')}>
                                        {this.props.data.customer.businessName}
                                    </Descriptions.Item>
                                    <Descriptions.Item label={t('customerClass.vatId')}>
                                        {this.props.data.customer.vatNumber}
                                    </Descriptions.Item>
                                    <Descriptions.Item label={t('customerClass.registeredOffice')}>
                                        {this.props.data.customer.addressSociety} - {this.props.data.customer.citySociety} (
                                        {this.props.data.customer.provinceSocietyId}) - {this.props.data.customer.postalCodeSociety}
                                    </Descriptions.Item>
                                </>
                            )}
                            <Descriptions.Item label={t('commonClass.contactDetails')}>
                                {this.props.data.customer.isBusiness === 1 ? (
                                    <>
                                        <strong>{t('customerClass.pec')} : </strong>
                                        {this.props.data.customer.pec === '' ? (
                                            <i>({t('commonClass.notCompiled')})</i>
                                        ) : (
                                            this.props.data.customer.pec
                                        )}
                                        <br />
                                        <strong>{t('customerClass.sdi')} : </strong>
                                        {this.props.data.customer.sdi === undefined ? (
                                            <i>({t('commonClass.notCompiled')})</i>
                                        ) : (
                                            this.props.data.customer.sdi
                                        )}
                                        <br />
                                    </>
                                ) : (
                                    ''
                                )}
                                <strong>{t('customerClass.tel1')} :</strong> {this.props.data.customer.tel1}
                                <br />
                                <strong>{t('customerClass.tel2')} :</strong>{' '}
                                {this.props.data.customer.tel2 === '' ? (
                                    <i>({t('commonClass.notCompiled')})</i>
                                ) : (
                                    this.props.data.customer.tel2
                                )}
                                <br />
                                <strong>{t('customerClass.emailAddress')} :</strong> {this.props.data.customer.email}
                            </Descriptions.Item>
                        </Descriptions>
                    </Col>
                </Row>
                <Row>
                    <Col xs={24} sm={24} md={12} lg={8} xl={8}>
                        <Descriptions
                            bordered
                            column={{ xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}
                            style={{ padding: 5 }}
                            title={t('commonClass.contacts')}
                        >
                            <Descriptions.Item label={t('commonClass.contact')}>
                                {this.props.data.contact.firstname} {this.props.data.contact.surname}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('commonClass.contactDetails')}>
                                <strong>{t('customerClass.tel1')} :</strong> {this.props.data.contact.tel1}
                                <br />
                                <strong>{t('customerClass.tel2')} :</strong>{' '}
                                {this.props.data.contact.tel2 === '' ? (
                                    <i>({t('commonClass.notCompiled')})</i>
                                ) : (
                                    this.props.data.contact.tel2
                                )}
                                <br />
                                <strong>{t('customerClass.emailAddress')} :</strong> {this.props.data.contact.email}
                                <br />
                            </Descriptions.Item>
                        </Descriptions>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={8} xl={8}>
                        {this.props.data.package.professional ? (
                            <Descriptions
                                bordered
                                column={{ xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}
                                style={{ padding: 5 }}
                                title={t('commonClass.installer')}
                            >
                                <Descriptions.Item label={t('customerClass.businessName')}>
                                    {this.props.data.installer.businessName}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('customerClass.address')}>
                                    {this.props.data.installer.address}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('commonClass.contacts')}>
                                    {this.props.data.installer.contacts}
                                </Descriptions.Item>
                            </Descriptions>
                        ) : (
                            <Descriptions
                                bordered
                                column={{ xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}
                                style={{ padding: 5 }}
                                title={t('commonClass.shipment')}
                            >
                                <Descriptions.Item label={t('commonClass.city')}>{this.props.data.shipping.city}</Descriptions.Item>
                                <Descriptions.Item label={t('customerClass.postalCode')}>
                                    {this.props.data.shipping.postalCode}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('customerClass.address')}>{this.props.data.shipping.address}</Descriptions.Item>
                                <Descriptions.Item label={t('commonClass.referenceName')}>
                                    {this.props.data.shipping.referenceName}
                                </Descriptions.Item>
                            </Descriptions>
                        )}
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={8} xl={8}>
                        <Descriptions
                            bordered
                            column={{ xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}
                            style={{ padding: 5 }}
                            title={t('commonClass.paymentData')}
                        >
                            <Descriptions.Item label={t('customerClass.payments.paymentType')}>
                                {this.props.data.payment.paymentType}
                            </Descriptions.Item>
                            {this.props.data.payment.paymentTypeId.toString() === '1' ? (
                                <>
                                    <Descriptions.Item label={t('customerClass.payments.accountHolder')}>
                                        {this.props.data.payment.accountHolder}
                                    </Descriptions.Item>
                                    <Descriptions.Item label={t('customerClass.payments.cardNumber')}>
                                        {this.props.data.payment.circuit} ***{' '}
                                        {this.props.data.payment.cardNumber?.substr(this.props.data.payment.cardNumber?.length - 4)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label={t('customerClass.payments.cardExpirationDate')}>
                                        {this.props.data.payment.cardExpirationDate!.format('MM/yyyy')}
                                    </Descriptions.Item>
                                </>
                            ) : (
                                <>
                                    <Descriptions.Item label={t('customerClass.payments.iban')}>
                                        {this.props.data.payment.iban}
                                    </Descriptions.Item>
                                    <Descriptions.Item label={t('customerClass.payments.swift')}>
                                        {this.props.data.payment.swift === undefined ? (
                                            <i>({t('commonClass.notCompiled')})</i>
                                        ) : (
                                            this.props.data.payment.swift
                                        )}
                                    </Descriptions.Item>
                                    <Descriptions.Item label={t('customerClass.payments.accountHolder')}>
                                        {this.props.data.payment.accountHolder}
                                    </Descriptions.Item>
                                    <Descriptions.Item label={t('customerClass.payments.accountSignatory')}>
                                        {this.props.data.payment.accountSignatory}
                                    </Descriptions.Item>
                                    <Descriptions.Item label={t('customerClass.fiscalIdSN')}>
                                        {this.props.data.payment.fiscalId}
                                    </Descriptions.Item>
                                    <Descriptions.Item label={t('customerClass.address')}>
                                        {this.props.data.payment.address} - {this.props.data.payment.city} (
                                        {this.props.data.payment.province}) - {this.props.data.payment.postalCode} -{' '}
                                        {this.props.data.payment.nationId}
                                    </Descriptions.Item>
                                </>
                            )}
                        </Descriptions>
                    </Col>
                </Row>
            </div>
        );
    }
}

export default ContractSummary;
